import type { CreatureDefenseFoldInput } from '../types';
import { phpJsonString } from './php-json-values';

const DAMAGE_SOURCE_KEYS = [
    'source_item_id', 'source_name', 'source_kind', 'source_origin_item_id', 'source_origin_name',
] as const;
const STATUS_SOURCE_KEYS = [
    ...DAMAGE_SOURCE_KEYS, 'source_status_id', 'source_inventory_id', 'source_thumb_url',
] as const;

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

export function normalizeDefenseSource(
    source: Readonly<Record<string, unknown>>,
    family: CreatureDefenseFoldInput['family'],
): Record<string, string> {
    const normalized: Record<string, string> = {};
    for (const key of family === 'damage' ? DAMAGE_SOURCE_KEYS : STATUS_SOURCE_KEYS) {
        const value = source[key];
        if ((typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
            && value !== '' && value !== '0' && value !== 0 && value !== false) {
            normalized[key] = phpJsonString(value);
        }
    }
    return normalized;
}

/** First-wins writer; untouched legacy entries keep their original shape until matched. */
export function foldCreatureDefenseEntries(input: CreatureDefenseFoldInput): unknown[] {
    const identityKey = input.family === 'damage' ? 'damage_type' : 'item_layer_effect';
    const entries = input.initialEntries.slice();

    for (const event of input.events) {
        if (event.uuid === '') continue;
        const normalized = normalizeDefenseSource(event.source, input.family);
        let matched = false;
        for (let index = 0; index < entries.length; index += 1) {
            const existing = entries[index];
            const row = recordOf(existing);
            const existingUuid = row ? String(row[identityKey] ?? '') : typeof existing === 'string' ? existing : '';
            if (existingUuid !== event.uuid) continue;
            matched = true;
            if (!row) {
                entries[index] = { [identityKey]: event.uuid, ...normalized, all_sources: [] };
                break;
            }
            const sourceId = normalized.source_item_id;
            if (sourceId === undefined && input.family === 'status') break;
            const allSources = Array.isArray(row.all_sources) ? row.all_sources : [];
            const duplicate = sourceId !== undefined && (row.source_item_id === sourceId || allSources.some(source => (
                recordOf(source)?.source_item_id === sourceId
            )));
            if (!duplicate && Object.keys(normalized).length > 0) {
                entries[index] = { ...row, all_sources: [...allSources, normalized] };
            }
            break;
        }
        if (!matched) entries.push({ [identityKey]: event.uuid, ...normalized, all_sources: [] });
    }
    return entries;
}
